import React, { useState, useEffect } from 'react';
import DashboardWrapper from '../components/DashboardWrapper';
import api from '../lib/api';
import { Link } from 'react-router-dom';
import { Heart, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import resolveProduct from '../domain/product/resolveProduct';

const FavorisPage = () => {
  const [favorites, setFavorites] = useState([]);
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFavorites();
  }, []);

  const loadFavorites = async () => {
    try {
      const response = await api.get('/favorites?type=favori');
      setFavorites(response.data);

      // Load product details for each favorite
      const productPromises = response.data.map(fav => 
        api.get(`/products/${fav.product_id}`).catch(() => null)
      );
      const productResponses = await Promise.all(productPromises);
      
      const productMap = {};
      productResponses.forEach((res, index) => {
        if (res) {
          productMap[response.data[index].product_id] = res.data;
        }
      });
      setProducts(productMap);
    } catch (error) {
      console.error('Error loading favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFavorite = async (favoriteId) => {
    try {
      await api.delete(`/favorites/${favoriteId}`);
      toast.success('Retiré de la Wishlist');
      loadFavorites();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <DashboardWrapper>
      {/* Container unifié 1200px - cohérent avec le design system */}
      <div 
        className="space-y-6"
        style={{
          maxWidth: '1200px',
          marginLeft: 'auto',
          marginRight: 'auto',
          paddingLeft: '16px',
          paddingRight: '16px',
          paddingTop: '32px',
          paddingBottom: '32px',
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight" data-testid="page-title">
            Ma Wishlist
          </h1>
          <p className="text-muted-foreground mt-2">
            Retrouvez les jeux enregistrés dans votre Wishlist
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : favorites.length === 0 ? (
          <div className="bg-white rounded-lg border p-12 text-center">
            <Heart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground" data-testid="no-favorites-message">
              Votre Wishlist est vide pour le moment
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Ajoutez des produits à votre Wishlist pour les retrouver facilement ici
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {favorites.map((favorite) => {
              const product = products[favorite.product_id];
              if (!product) return null;

              return (
                <div
                  key={favorite.id}
                  className="bg-white rounded-lg border p-4 flex gap-4"
                  data-testid={`favorite-item-${favorite.id}`}
                >
                  <Link to={`/dashboard/produit/${product.id}`} className="flex-shrink-0">
                    <div className="w-24 h-24 bg-slate-50 rounded-lg overflow-hidden flex items-center justify-center">
                      {product.visuels && product.visuels.length > 0 ? (
                        <img src={product.visuels[0]} alt={product.intitule} className="max-w-full max-h-full object-contain" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                          Pas d'image
                        </div>
                      )}
                    </div>
                  </Link>

                  <div className="flex-1 min-w-0">
                    <Link to={`/dashboard/produit/${product.id}`}>
                      <h3 className="font-semibold hover:text-primary transition-colors">{product.intitule}</h3>
                    </Link>
                    <p className="text-sm text-muted-foreground mb-2">{resolveProduct(product).editors.displayLabel || '—'}</p>
                    <div className="flex items-center gap-4">
                      <div>
                        <span className="text-lg font-bold">{(() => { const rp = resolveProduct(product).pricing; return rp.hasAnyPrice ? rp.priceLabel : '—'; })()}</span>
                        <span className="text-xs text-muted-foreground ml-2">PVC: {product.prix_vente_conseille}€</span>
                      </div>
                      <span className="px-2 py-1 text-xs font-medium rounded-md bg-secondary capitalize">
                        {favorite.type === 'favori' ? 'Wishlist' : favorite.type === 'a_suivre' ? 'À suivre' : 'Autre catégorie'}
                      </span>
                    </div>
                    {favorite.commentaire && (
                      <p className="text-sm text-muted-foreground mt-2 italic">
                        "{favorite.commentaire}"
                      </p>
                    )}
                  </div>

                  <div className="flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveFavorite(favorite.id)}
                      data-testid={`remove-favorite-${favorite.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardWrapper>
  );
};

export default FavorisPage;
